import type { DayItem, DayStep } from '../api/client.ts';
import { itemDone, itemPartial, stepsDone } from './day-model.ts';
import { Icon } from './Icon.tsx';

interface Props {
  item: DayItem;
  open: boolean;
  onToggleItem: (itemId: string) => void;
  onToggleStep: (itemId: string, stepId: string) => void;
  onToggleOpen: (itemId: string) => void;
  /**
   * Opens this item's sheet. The recurrence in words travels with it, because
   * the sheet needs the SERVER'S phrasing of a rule its own four chips cannot
   * say, and nothing in this client may phrase one itself (AGENTS.md).
   */
  onEdit: (itemId: string, meta: string) => void;
}

/**
 * The tick is a 44px square around a 23px circle, so the visible control stays
 * small while the hit area stays large. `role="checkbox"` with `aria-checked`
 * rather than a real input: the circle, its fill and its half state are drawn,
 * and a native checkbox brings a control that cannot be. The half state is
 * `mixed`, so what the circle draws is also what is announced.
 */
function Tick({
  checked,
  label,
  onClick,
}: {
  checked: boolean | 'mixed';
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      className="tick"
      role="checkbox"
      aria-checked={checked}
      aria-label={label}
      onClick={onClick}
    >
      <i />
    </button>
  );
}

function StepRow({ step, onToggle }: { step: DayStep; onToggle: () => void }) {
  return (
    <li className={step.done ? 'item done' : 'item'}>
      <Tick checked={step.done} label={step.title} onClick={onToggle} />
      <div className="item-main">
        <div className="item-txt">
          <div className="item-title">{step.title}</div>
        </div>
      </div>
    </li>
  );
}

export function ItemRow({ item, open, onToggleItem, onToggleStep, onToggleOpen, onEdit }: Props) {
  const steps = item.steps ?? [];
  const hasSteps = steps.length > 0;
  const done = itemDone(item);
  const partial = itemPartial(item);

  const className = ['item', done ? 'done' : '', partial ? 'partial' : '', open ? 'open' : '']
    .filter(Boolean)
    .join(' ');

  // `:empty` hides the meta line in CSS, but an element containing only
  // whitespace is not empty - so it is not rendered at all when there is
  // nothing to say.
  const showWords = item.meta !== undefined && item.meta !== '';
  const hasMeta = showWords || item.once === true || item.evening === true;
  const showNoteDot = item.hasNote === true && !hasSteps;

  // What the row SAYS, built from the same four conditions the row DRAWS from
  // and in the same order, so the two cannot drift apart. This is the ONLY
  // place the two glyph markers are put into words: the button's label replaces
  // everything inside it for a screen reader, so anything left out here is not
  // said at all, and a second copy anywhere below would be words nobody hears.
  const spoken = [
    item.title,
    ...(item.evening === true ? ['this evening'] : []),
    ...(showWords ? [item.meta] : []),
    ...(item.once === true ? ['once'] : []),
    ...(showNoteDot ? ['has a note'] : []),
  ].join(', ');

  return (
    <li className={className}>
      <Tick
        checked={partial ? 'mixed' : done}
        label={item.title}
        onClick={() => onToggleItem(item.id)}
      />

      <div className="item-main">
        {/* The row's text is the way into its sheet, as the approved design
            has it. The BUTTON is the text and not the whole of `.item-main`,
            which also holds the steps toggle - a button inside a button is not
            a thing, and the two would fight over the same tap.

            Labelled rather than named by its content, which is the opposite of
            the card head above it and right for the opposite reason: this row
            already has a control called `<title>` - the tick - and two of them
            would be indistinguishable to anybody reading the row through a
            screen reader. There is no heading here to rename.

            The label carries the WHOLE row, not just the title: a label
            replaces the content it is put on, so `Edit <title>` alone would
            silence the recurrence, the once flag and the two markers drawn as
            glyphs. Those markers are words in the label and nowhere else -
            sr-only spans for them sat here until the label grew, and a label
            replaces them, so all they could do was drift out of step with the
            sentence actually spoken. */}
        <button
          className="item-txt"
          aria-label={`Edit ${spoken}`}
          onClick={() => onEdit(item.id, item.meta)}
        >
          {/* Spans rather than divs, because they are inside a button now: a
              button takes phrasing content, and `.item-title` carries its own
              `display: block` for it. */}
          <span className="item-title">{item.title}</span>
          {(hasMeta || showNoteDot) && (
            <span className="item-meta">
              {/* "This evening" is a quiet marker, not a bucket: the day is
                  already grouped by schedule, and an evening bucket would cut
                  across those groups (AGENTS.md). */}
              {item.evening === true && <Icon name="moon" />}
              {showWords && <span>{item.meta}</span>}
              {item.once === true && <span className="onceflag">once</span>}
              {showNoteDot && (
                <span className="notedot">
                  <Icon name="note" />
                </span>
              )}
            </span>
          )}
        </button>

        {hasSteps && (
          <button
            className="item-expand"
            aria-expanded={open}
            aria-label={`${open ? 'Hide' : 'Show'} the ${steps.length} steps in ${item.title}`}
            onClick={() => onToggleOpen(item.id)}
          >
            <span className="steps-badge tnum">
              {stepsDone(item)}/{steps.length}
            </span>
            <span className="item-fold">
              <Icon name="chevR" />
            </span>
          </button>
        )}
      </div>

      {hasSteps && (
        // Opening is a grid-rows transition, so the steps stay in the DOM at
        // zero height. `inert` is what stops a closed item keeping focusable
        // ticks, exactly as a folded card's body does.
        <div className="steps-wrap" inert={!open}>
          <div>
            <div className="steps">
              <ul>
                {steps.map((step) => (
                  <StepRow
                    key={step.id}
                    step={step}
                    onToggle={() => onToggleStep(item.id, step.id)}
                  />
                ))}
              </ul>
              {item.note !== undefined && item.note !== '' && (
                <div className="stepnote">{item.note}</div>
              )}
            </div>
          </div>
        </div>
      )}
    </li>
  );
}
